import { Badge } from "design-system/atoms/Badge"
import { MessageSquareText, Milestone } from "lucide-react"
import { Loading } from "modules/shared/components/Loading"
import { useConversationQuestionsQuery } from "modules/support/hooks/useConversationQuestionsQuery"
import { useConversationTicketsQuery } from "modules/support/hooks/useConversationTicketsQuery"
import { useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { CustomerIssues } from "../entities/CustomerIssues"
import { QuestionContent } from "./QuestionContent"
import { TicketContent } from "./TicketContent"

type Props = {
  customerId: string
  onConversationClick: (conversationId: string) => void
  selectedQuestionId?: string
  createdBefore?: string
  selectedIssueId?: string
  shouldShowIssues?: boolean
}

const PAGE_SIZE = 100

export function SupportTimeline({
  customerId,
  onConversationClick,
  selectedQuestionId,
  createdBefore,
  selectedIssueId,
  shouldShowIssues = false,
}: Props) {
  const { t } = useTranslation()

  const { data: tickets, isPending: isTicketsPending } = useConversationTicketsQuery({
    query: {
      page: 1,
      total: PAGE_SIZE,
      filterBy: { customerId },
      sortBy: { field: "createdAt", order: "asc" },
    },
  })
  const { data: questions, isPending: isQuestionsPending } = useConversationQuestionsQuery({
    query: {
      page: 1,
      total: PAGE_SIZE,
      filterBy: { customerId },
      sortBy: { field: "createdAt", order: "asc" },
    },
  })

  const sortedTicketsAndQuestions = useMemo(
    () =>
      [...(tickets ?? []), ...(questions ?? [])]
        .filter((item) => !createdBefore || item.createdAt < createdBefore)
        .sort((a, b) => (a.createdAt > b.createdAt ? 1 : -1)),
    [createdBefore, tickets, questions],
  )

  const isPending = isTicketsPending || isQuestionsPending
  const hasData = sortedTicketsAndQuestions.length > 0

  const bottomRef = useRef<HTMLDivElement>(null)
  const selectedQuestionRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (isPending || !hasData) return

    if (selectedQuestionId && selectedQuestionRef.current) {
      selectedQuestionRef.current.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }

    bottomRef.current?.scrollIntoView({ behavior: "instant" })
  }, [isPending, hasData, selectedQuestionId])

  const customerIssues = shouldShowIssues ? (
    <CustomerIssues
      customerId={customerId}
      selectedIssueId={selectedIssueId}
      onConversationClick={onConversationClick}
      createdAtFrom={createdBefore}
      hideEmptyState={isPending || hasData}
    />
  ) : null

  if (isPending)
    return (
      <>
        <Loading />
        {customerIssues}
      </>
    )

  if (!hasData && shouldShowIssues) return customerIssues

  if (!hasData)
    return (
      <div className="flex flex-col gap-2 px-4 py-8 items-center justify-center text-center max-w-md mx-auto">
        <Milestone className="size-10 text-foreground" />
        <div className="flex flex-col gap-1">
          <span className="text-lg">{t("support.timeline.noData")}</span>
          <span className="text-sm text-muted-foreground">
            {t("support.timeline.noDataDescription")}
          </span>
        </div>
      </div>
    )

  // Build a flat list of timeline nodes: conversation-start entries + items
  type ConversationNode = {
    kind: "conversation"
    conversationId: string
    campaignName: string | null | undefined
    createdAt: string
  }
  type ItemNode = {
    kind: "item"
    item: (typeof sortedTicketsAndQuestions)[number]
  }
  type TimelineNode = ConversationNode | ItemNode

  const nodes: TimelineNode[] = []
  let lastConversationId: string | null = null

  for (const item of sortedTicketsAndQuestions) {
    if (item.conversationId !== lastConversationId) {
      nodes.push({
        kind: "conversation",
        conversationId: item.conversationId,
        campaignName: item.campaign?.name,
        createdAt: item.createdAt,
      })
      lastConversationId = item.conversationId
    }
    nodes.push({ kind: "item", item })
  }

  return (
    <>
      <div className="group relative py-4">
        {shouldShowIssues && (
          <div className="px-4 pb-3">
            <Badge variant="secondary">{t("support.timeline.oldFormat")}</Badge>
          </div>
        )}
        {nodes.map((node, index) => {
          const isLast = index === nodes.length - 1

          if (node.kind === "conversation") {
            return (
              <div
                key={`conv-${node.conversationId}`}
                className="relative flex w-full cursor-pointer group/conv"
                onClick={() => onConversationClick(node.conversationId)}
              >
                <div className="absolute flex flex-col items-center left-4 top-0 mt-3.5 h-full z-10">
                  <div className="shrink-0 size-2.5 rounded-full bg-muted-foreground/40 z-10 transition-colors duration-150 group-hover/conv:bg-muted-foreground" />
                  {!isLast && <div className="w-0.5 bg-border flex-1" />}
                </div>
                <div className="w-full py-2 pr-4 pl-12 flex items-center gap-2 rounded-lg transition-colors duration-150 group-hover/conv:bg-secondary">
                  <MessageSquareText className="size-3.5 text-muted-foreground shrink-0" />
                  <span className="text-xs font-medium text-muted-foreground truncate">
                    {node.campaignName ?? t("campaigns.untitled")}
                  </span>
                </div>
              </div>
            )
          }

          const { item } = node
          const itemKind = "summary" in item ? "question" : "ticket"
          const isSelectedQuestion =
            itemKind === "question" && selectedQuestionId && String(item.id) === selectedQuestionId

          return (
            <div
              key={`${item.id}-${itemKind}`}
              className="group/item relative flex w-full"
              ref={isSelectedQuestion ? selectedQuestionRef : undefined}
            >
              <div className="absolute flex flex-col items-center left-4 top-0 mt-6 h-full z-10">
                <div className="shrink-0 size-2.5 bg-support-secondary rounded-full z-10 transition-colors duration-150 group-hover/item:bg-support" />
                {!isLast && <div className="w-0.5 bg-border flex-1" />}
              </div>
              <div className="transition-colors duration-150 rounded-lg w-full py-4 pr-4 pl-12 h-auto items-start justify-start whitespace-normal">
                {"summary" in item ? (
                  <QuestionContent
                    question={item}
                    key={`${item.id}-${itemKind}`}
                    highlight={!!isSelectedQuestion}
                  />
                ) : (
                  <TicketContent ticket={item} key={`${item.id}-${itemKind}`} />
                )}
              </div>
            </div>
          )
        })}
        <div ref={bottomRef} />
      </div>
      {customerIssues}
    </>
  )
}
